import { gql } from '@apollo/client';
import { useCallback } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ScheduleDetail } from '~components/schedules/ScheduleDetail';
import { Page } from '~components/ui/Page';
import { PageContent } from '~components/ui/PageContent';
import { PageLoader } from '~components/ui/PageLoader';
import { useConditionalPolling } from '~components/useConditionalPolling';
import { HandleApiError } from '~graphql/HandleApiError';
import { ensure } from '~utils/types';
import { useScheduleDetailsPageQuery } from './__generated__/[id].graphql';

const POLL_INTERVAL = 5000;

export function ScheduleDetailsPage() {
  // The URL param keeps its original name so the non-Staff power schedule route,
  // which reads `powerScheduleId`, keeps working behind the same URL.
  const { customer, powerScheduleId: scheduleId } = useParams();
  const { data, loading, error, refetch, startPolling, stopPolling } = useScheduleDetailsPageQuery({
    variables: {
      customerHandle: ensure(customer),
      scheduleId: ensure(scheduleId),
    },
    pollInterval: POLL_INTERVAL,
  });

  useConditionalPolling({ startPolling, stopPolling, pollInterval: POLL_INTERVAL });

  const handleScheduleUpdateSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <Page title={`${data?.schedule?.title || 'Loading…'} | Schedule`} pageName="schedule_detail">
      <PageContent>
        {loading ? (
          <PageLoader />
        ) : error ? (
          <HandleApiError error={error} />
        ) : !data?.schedule ? (
          <Navigate to="../schedules/404" replace />
        ) : (
          data.customer && (
            <ScheduleDetail
              customer={data.customer}
              schedule={data.schedule}
              apps={data.organization?.appSubscriptions ?? []}
              onScheduleUpdateSuccess={handleScheduleUpdateSuccess}
            />
          )
        )}
      </PageContent>
    </Page>
  );
}

ScheduleDetailsPage.graphql = {
  queries: {
    ScheduleDetailsPage: gql`
      query ScheduleDetailsPage($customerHandle: String!, $scheduleId: ID!) {
        customer: customerByHandle(handle: $customerHandle) {
          id
          ...ScheduleDetail_customer
        }
        schedule(id: $scheduleId) {
          id
          ...ScheduleDetail_schedule
        }
        organization {
          id
          appSubscriptions {
            id
            applicationId
            name
          }
        }
      }
    `,
  },
};
